/**
 * FL-47: 管理者ユーザが自社のユーザを登録できる機能
 * CompanyUsersPage - 会社ユーザー管理画面
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserPlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';
import { CompanyUserService, CompanyUser } from '../../services/companyUserService';
import { PageTitle } from '../../components/ui/PageTitle';
import { EmailDuplicateValidator } from '../../components/forms/EmailDuplicateValidator';
import { isSupabaseAvailable } from '../../utils/supabase';
import { isDemoMode } from '../../utils/environment';
import logger from '../../utils/logger';
import {
  validateEmail,
  validateRequired,
  EMAIL_VALIDATION_MESSAGE,
  REQUIRED_VALIDATION_MESSAGE,
} from '../../utils/validation';
import { getCompanyRoleLabel, getRoleColor } from '../../utils/roleUtils';
import { UserDetailModal } from '../../components/admin/UserDetailModal';

// UserFormModal実装
interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
  user?: CompanyUser | null;
}

interface UserFormData {
  email: string;
  name: string;
  role: string;
  password?: string;
  confirmPassword?: string;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, onSave, user }) => {
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'dispatcher',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);

  // フォームデータを初期化
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        email: user.email || '',
        name: user.name || '',
        role: user.role || 'dispatcher',
        password: '', // 編集時はパスワードは空に
        confirmPassword: '',
      });
    } else if (isOpen) {
      setFormData({
        email: '',
        name: '',
        role: 'dispatcher',
        password: '',
        confirmPassword: '',
      });
    }
    setErrors({});
    setIsEmailDuplicate(false);
    setIsEmailValid(false);
  }, [user, isOpen]);

  // モーダルが閉じられた時の追加リセット処理
  useEffect(() => {
    if (!isOpen) {
      // モーダルが閉じられたときは状態を完全にリセット
      setFormData({
        email: '',
        name: '',
        role: 'dispatcher',
        password: '',
        confirmPassword: '',
      });
      setErrors({});
      setIsEmailDuplicate(false);
      setIsEmailValid(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  // バリデーション
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.email)) {
      newErrors.email = REQUIRED_VALIDATION_MESSAGE;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = EMAIL_VALIDATION_MESSAGE;
    }

    if (!formData.name.trim()) {
      newErrors.name = '名前は必須です';
    }

    if (!formData.role) {
      newErrors.role = '権限は必須です';
    }

    // パスワードバリデーション
    if (!user) {
      // 新規ユーザー作成時：パスワード必須
      if (!formData.password || formData.password.trim().length === 0) {
        newErrors.password = 'パスワードは必須です';
      } else if (formData.password.length < 8) {
        newErrors.password = 'パスワードは8文字以上である必要があります';
      } else {
        // パスワード強度チェック
        const hasLower = /[a-z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);

        if (!hasLower || !hasNumber) {
          newErrors.password = 'パスワードは英小文字と数字を含む必要があります';
        }
      }

      if (!formData.confirmPassword || formData.confirmPassword.trim().length === 0) {
        newErrors.confirmPassword = 'パスワード確認は必須です';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'パスワードが一致しません';
      }
    } else if (user) {
      // 編集時：パスワード入力があれば検証、なければ許可
      if (formData.password && formData.password.trim().length > 0) {
        if (formData.password.length < 8) {
          newErrors.password = 'パスワードは8文字以上である必要があります';
        } else {
          const hasLower = /[a-z]/.test(formData.password);
          const hasNumber = /\d/.test(formData.password);

          if (!hasLower || !hasNumber) {
            newErrors.password = 'パスワードは英小文字と数字を含む必要があります';
          }
        }

        // パスワード入力がある場合は確認パスワードも必須
        if (!formData.confirmPassword || formData.confirmPassword.trim().length === 0) {
          newErrors.confirmPassword = 'パスワード確認は必須です';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'パスワードが一致しません';
        }
      } else if (formData.confirmPassword && formData.confirmPassword.trim().length > 0) {
        // パスワードがなくて確認パスワードがある場合はエラー
        newErrors.confirmPassword = 'パスワードを入力してください';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 保存処理
  const handleSave = async () => {
    if (!validateForm()) return;

    // メール重複チェック
    if (isEmailDuplicate) {
      setErrors({ ...errors, email: 'このメールアドレスは既に使用されています' });
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      logger.error('Failed to save user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div
        data-testid='user-form-modal'
        className='bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto'
      >
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-6'>
          {user ? 'ユーザー編集' : 'ユーザー追加'}
        </h3>

        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave();
          }}
          className='space-y-4'
        >
          {/* メールアドレス（重複チェック付き） */}
          <EmailDuplicateValidator
            value={formData.email}
            onChange={value => setFormData(prev => ({ ...prev, email: value }))}
            excludeUserId={user?.id}
            label='メールアドレス'
            placeholder='user@example.com'
            required
            disabled={isLoading}
            onDuplicateStatusChange={setIsEmailDuplicate}
            onFormatValidityChange={setIsEmailValid}
          />
          {errors.email && (
            <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.email}</p>
          )}

          {/* 名前 */}
          <div>
            <label
              htmlFor='name'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              名前 <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              id='name'
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              placeholder='ユーザー名'
              disabled={isLoading}
              autoComplete='name'
            />
            {errors.name && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.name}</p>
            )}
          </div>

          {/* 権限 */}
          <div>
            <label
              htmlFor='role'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              権限 <span className='text-red-500'>*</span>
            </label>
            <select
              id='role'
              value={formData.role}
              onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              disabled={isLoading}
            >
              <option value='dispatcher'>配車・営業担当者</option>
              <option value='readonly'>閲覧専用</option>
            </select>
            {errors.role && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.role}</p>
            )}
          </div>

          {/* パスワード */}
          <div>
            <label
              htmlFor='password'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              パスワード {!user && <span className='text-red-500'>*</span>}
            </label>
            <input
              type='password'
              id='password'
              autoComplete='new-password'
              value={formData.password || ''}
              onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              placeholder={
                user
                  ? '変更しない場合は空白のままにしてください'
                  : '8文字以上、英小文字と数字を含む'
              }
              disabled={isLoading}
            />
            {errors.password && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.password}</p>
            )}
            {user ? (
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                パスワードを変更する場合のみ入力してください
              </p>
            ) : (
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                8文字以上、英小文字と数字を含む必要があります
              </p>
            )}
          </div>

          {/* パスワード確認 */}
          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              パスワード確認{' '}
              {(!user || (user && formData.password)) && <span className='text-red-500'>*</span>}
            </label>
            <input
              type='password'
              id='confirmPassword'
              autoComplete='new-password'
              value={formData.confirmPassword || ''}
              onChange={e => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              placeholder={
                user ? '変更しない場合は空白のままにしてください' : '上記と同じパスワードを入力'
              }
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* アクションボタン */}
          <div className='flex justify-end gap-3 pt-4'>
            <button
              data-testid='cancel-button'
              type='button'
              onClick={onClose}
              disabled={isLoading}
              className='px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 
                         rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 
                         disabled:opacity-50 disabled:cursor-not-allowed'
            >
              キャンセル
            </button>
            <button
              data-testid='save-button'
              type='submit'
              disabled={isLoading || isEmailDuplicate || !isEmailValid}
              className='px-4 py-2 bg-primary-600 button-primary-fallback text-white rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600
                         disabled:opacity-50 disabled:cursor-not-allowed flex items-center'
            >
              {isLoading && (
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
              )}
              {user ? '更新' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CompanyUsersPage: React.FC = () => {
  const { user } = useAuth();
  const isDemo = isDemoMode();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    userId: string;
    userName: string;
  } | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);

  const companyUserService = useMemo(() => {
    try {
      return new CompanyUserService();
    } catch (error) {
      logger.error('Failed to initialize CompanyUserService:', error);
      return null;
    }
  }, []);

  // Supabase接続チェック
  useEffect(() => {
    const checkSupabaseConnection = () => {
      const available = isSupabaseAvailable();

      if (!available) {
        setError('データベース接続が利用できません。環境設定を確認してください。');
        setIsLoading(false);
      }
    };

    checkSupabaseConnection();
  }, []);

  // データ読み込み
  const loadData = async () => {
    if (!user?.companyId) return;

    if (!companyUserService) {
      setError('サービスが初期化できませんでした。データベース接続を確認してください。');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const usersData = await companyUserService.getCompanyUsers(user.companyId);
      setUsers(usersData);
    } catch (err) {
      logger.error('Failed to load user data:', err);
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  // 成功メッセージの自動クリア
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [successMessage]);

  // フィルタリング済みユーザー
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch =
        !searchTerm ||
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = roleFilter === 'all' || u.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, roleFilter]);

  // 管理者権限チェック
  if (!user || ![Role.ADMIN, Role.SUPER_ADMIN].includes(user.role)) {
    return <Navigate to='/' replace />;
  }

  // ユーザー作成/更新
  const handleSaveUser = async (userData: any) => {
    if (!companyUserService) {
      setError('サービスが初期化できませんでした。データベース接続を確認してください。');
      return;
    }

    try {
      if (editingUser) {
        // 編集の場合：パスワード更新に対応（currentUserId を渡す）
        // FL-209: パスワード変更時は管理者・配車担当者の権限チェックがサービス層で実施される
        await companyUserService.updateUser(
          editingUser.id,
          userData,
          user.id // 編集権限のある管理者・配車担当者のID
        );
        setSuccessMessage('ユーザーを更新しました');
      } else {
        // 新規作成の場合：パスワードを含めて作成
        await companyUserService.createUser(
          { ...userData, companyId: user.companyId! },
          user.id,
          userData.password // パスワードを第3引数として渡す
        );
        setSuccessMessage('ユーザーを作成しました');
      }

      await loadData();
      setIsUserModalOpen(false);
      setEditingUser(null);

      // 成功メッセージを3秒後に消す - useEffectで管理するため一旦コメントアウト
      // setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      logger.error('Failed to save user:', err);
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    }
  };

  // ユーザー削除
  const handleDeleteUser = async (userId: string) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;

    setDeleteConfirm({
      show: true,
      userId,
      userName: targetUser.name,
    });
  };

  // 削除確認後の処理
  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    if (!companyUserService) {
      setError('サービスが初期化できませんでした。データベース接続を確認してください。');
      return;
    }

    try {
      await companyUserService.deleteUser(deleteConfirm.userId);
      setSuccessMessage('ユーザーを削除しました');
      setDeleteConfirm(null);
      await loadData();

      // setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      logger.error('Failed to delete user:', err);
      setError(err instanceof Error ? err.message : '削除に失敗しました');
    }
  };

  if (isLoading) {
    return (
      <div className='max-w-7xl mx-auto'>
        <PageTitle icon={UsersIcon} title='ユーザー管理' />
        <div className='text-center py-12'>
          <p className='text-gray-500 dark:text-gray-400'>読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-7xl mx-auto'>
      <PageTitle icon={UsersIcon} title='ユーザー管理' />

      {/* 成功メッセージ */}
      {successMessage && (
        <div className='mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg'>
          <div className='flex items-center'>
            <CheckCircleIcon className='h-5 w-5 text-green-600 dark:text-green-400 mr-2' />
            <p className='text-green-700 dark:text-green-300'>{successMessage}</p>
          </div>
        </div>
      )}

      {/* エラーメッセージ */}
      {error && (
        <div className='mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
          <div className='flex items-center'>
            <XCircleIcon className='h-5 w-5 text-red-600 dark:text-red-400 mr-2' />
            <p className='text-red-700 dark:text-red-300'>{error}</p>
          </div>
        </div>
      )}

      {/* デモモード制限通知 */}
      {isDemo && (
        <div className='mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
          <div className='flex items-center'>
            <UsersIcon className='h-5 w-5 text-blue-600 dark:text-blue-400 mr-2' />
            <p className='text-blue-700 dark:text-blue-300'>
              デモ体験では、ユーザーの追加・編集・削除機能は無効化されています。
            </p>
          </div>
        </div>
      )}

      {/* 検索・フィルター・アクション */}
      <div className='mb-6 flex flex-col sm:flex-row gap-4'>
        {/* 検索 */}
        <div className='flex-1 relative'>
          <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
          <input
            data-testid='search-input'
            type='text'
            placeholder='ユーザー名またはメールアドレスで検索...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
            autoComplete='off'
          />
        </div>

        {/* 権限フィルター */}
        <div className='relative'>
          <FunnelIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
          <select
            data-testid='role-filter'
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            aria-label='権限フィルター'
            className='pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
          >
            <option value='all'>すべての権限</option>
            <option value='admin'>管理者</option>
            <option value='dispatcher'>配車・営業担当者</option>
            <option value='driver'>陸送員</option>
            <option value='readonly'>閲覧専用</option>
          </select>
        </div>

        {/* 新規ユーザー追加ボタン */}
        <button
          data-testid='add-user-button'
          onClick={() => {
            if (isDemo) return;
            setEditingUser(null);
            setIsUserModalOpen(true);
          }}
          disabled={isDemo}
          title={isDemo ? 'デモモードでは使用できません' : ''}
          className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium ${
            isDemo ? 'opacity-50 cursor-not-allowed hover:bg-blue-600 dark:hover:bg-blue-500' : ''
          }`}
        >
          <UserPlusIcon className='h-5 w-5 mr-2' />
          新規ユーザー追加
        </button>
      </div>

      {/* 陸送員追加についてのお知らせ */}
      <div className='mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
        <div className='flex items-start'>
          <InformationCircleIcon className='h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0' />
          <div>
            <p className='text-blue-700 dark:text-blue-300 text-sm'>
              <span className='font-medium'>陸送員（ドライバー）の追加について：</span>
              陸送員を追加する場合は、
              <Link
                to='/master/land-transporters'
                className='inline-flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline font-medium transition-colors ml-1'
              >
                陸送員管理ページ
              </Link>
              をご利用ください。
            </p>
          </div>
        </div>
      </div>

      {/* ユーザー一覧テーブル */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
            <thead className='bg-gray-50 dark:bg-gray-900'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                  ユーザー
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                  権限
                </th>
              </tr>
            </thead>
            <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
              {filteredUsers.map(user => (
                <tr
                  key={user.id}
                  onClick={() => {
                    setSelectedUser(user);
                    setIsDetailModalOpen(true);
                  }}
                  className='hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer'
                >
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div>
                      <div className='text-sm font-medium text-gray-900 dark:text-white'>
                        {user.name}
                      </div>
                      <div className='text-sm text-gray-500 dark:text-gray-400'>{user.email}</div>
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}
                    >
                      {getCompanyRoleLabel(user.role)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 空状態 */}
        {filteredUsers.length === 0 && (
          <div className='text-center py-12'>
            <UsersIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
              ユーザーが見つかりません
            </h3>
            <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
              {searchTerm || roleFilter !== 'all'
                ? '検索条件を変更してください'
                : '最初のユーザーを追加してください'}
            </p>
          </div>
        )}
      </div>

      {/* ユーザー作成/編集モーダル */}
      <UserFormModal
        isOpen={isUserModalOpen}
        user={editingUser}
        onClose={() => {
          setIsUserModalOpen(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
      />

      {/* ユーザー詳細モーダル */}
      <UserDetailModal
        isOpen={isDetailModalOpen}
        user={selectedUser}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedUser(null);
        }}
        onEdit={user => {
          setIsDetailModalOpen(false);
          setEditingUser(user);
          setIsUserModalOpen(true);
        }}
        onDelete={userId => {
          setIsDetailModalOpen(false);
          handleDeleteUser(userId);
        }}
      />

      {/* 削除確認モーダル */}
      {deleteConfirm?.show && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6'>
            <div className='flex items-center mb-4'>
              <TrashIcon className='h-6 w-6 text-red-600 dark:text-red-400 mr-3' />
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>削除の確認</h3>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-6'>
              ユーザー「{deleteConfirm.userName}」を削除しますか？この操作は取り消せません。
            </p>
            <div className='flex justify-end space-x-3'>
              <button
                onClick={() => setDeleteConfirm(null)}
                className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                キャンセル
              </button>
              <button
                onClick={confirmDelete}
                className='px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center'
              >
                <TrashIcon className='h-4 w-4 mr-2' />
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyUsersPage;
